'use client';
import { useCallback, useEffect } from 'react';
import useHuddle01 from './useHuddle01';
import type { TDataMessage } from 'types/dist/common.types';
import type { VolatileDataMessage } from '@huddle01/web-core/types';

export const useDataMessage = (props?: {
  onMessage?: (payload: string, from: string, label?: string) => void;
  onVolatileMesssage?: (data: VolatileDataMessage) => void;
}) => {
  const { huddleClient } = useHuddle01();

  const localPeer = huddleClient.localPeer;

  /**
   * Send Data Gives the functionality to send data to other remote peers or the whole room,
   * NOTE: This will be sent over WebSockets which are over TCP which will be slower and can be rate limited.
   * @returns { success: boolean, error?: string } - error will be present if failed to send data
   */
  const sendData = useCallback(
    (data: Parameters<typeof localPeer.sendData>[0]) => {
      return localPeer.sendData(data);
    },
    [],
  );

  /**
   * Send Volatile Data Gives the functionality to send data to other remote peers or the whole room,
   * NOTE: This will be sent over WebRTC DataChannels which are over UDP which will be faster and can be rate limited.
   * @returns { ok: boolean } - if the volatile data was sent, cannot guarantee if the data was received as its over UDP
   */
  const sendVolatileData = useCallback(
    (data: Parameters<typeof localPeer.sendVolatileData>[0]) => {
      console.info('Sending Volatile Data', data);
      return huddleClient.localPeer.sendVolatileData(data);
    },
    [],
  );

  const handleNewMessage = useCallback(
    (data: TDataMessage) => {
      if (props?.onMessage)
        props.onMessage(data.payload, data.from, data.label);
    },
    [props?.onMessage],
  );

  const handleNewVolatileMessage = useCallback(
    (data: VolatileDataMessage) => {
      if (props?.onVolatileMesssage) props.onVolatileMesssage(data);
    },
    [props?.onVolatileMesssage],
  );

  useEffect(() => {
    localPeer.on('receive-data', handleNewMessage);

    huddleClient.room.on('received-volatile-data', handleNewVolatileMessage);

    return () => {
      localPeer.off('receive-data', handleNewMessage);

      huddleClient.room.off('received-volatile-data', handleNewVolatileMessage);
    };
  }, [props?.onMessage, props?.onVolatileMesssage]);

  return {
    sendData,
    sendVolatileData,
  };
};
